import React, { useCallback, useRef, useState } from "react";
import { useSelector } from "react-redux";
import "../../../css/googleAccount.css";
import {
    sendDataDynamicsLead,
    connectDynamicsLead,
} from "../../../helpers/Sources/dynamics365/dynamics365";
import LoginButton from "../../molecules/LoginButton";

/**
 * DynamicsLeadLogin - Component used as destination on Lead side
 * Used by DynamicsLeadDestination.js
 *
 * Flow:
 * 1. Store API is called -> returns auth_url + destination_id
 * 2. Popup opens -> user logs into Microsoft
 * 3. Code + state values are captured
 * 4. Connect API is called -> connection is completed
 */
const DynamicsLeadLogin = (props) => {
    const [isLoading, setIsLoading] = useState(false);
    const destinationId = useRef(null);
    const language = useSelector((state) => state.languagesState.languages);

    const getAuthUrl = useCallback(async () => {
        if (!props.crmUrl || !props.dynamicsName) return null;

        setIsLoading(true);
        props.setIsButtonLoader?.(true);

        try {
            const storePayload = {
                crm_url: props?.crmUrl,
                name: props?.dynamicsName,
            };
            const response = await sendDataDynamicsLead(storePayload);
            if (response.status) {
                const authUrl = response.data?.auth_url;
                const destId = response.data?.destination_id;
                if (authUrl && destId) {
                    destinationId.current = destId;
                    return authUrl;
                }
            }
            props.onError?.(response?.message ?? "Connection setup failed");
            return null;
        } catch (error) {
            props.onError?.(error?.message ?? "Connection setup failed");
            return null;
        } finally {
            if (!destinationId.current) {
                setIsLoading(false);
                props.setIsButtonLoader?.(false);
            }
        }
    }, [props]);

    const connect = useCallback(
        async ({ code, state }) => {
            try {
                const response = await connectDynamicsLead(destinationId.current, {
                    code,
                    state,
                });
                if (response.status) {
                    props.onSuccess?.({
                        destination_id: destinationId.current,
                        name: props.dynamicsName,
                        ...response.data,
                    });
                } else {
                    props.onError?.(response.message);
                }
            } catch (error) {
                props.onError?.(error?.message ?? "Connection failed");
            } finally {
                setIsLoading(false);
                props.setIsButtonLoader?.(false);
            }
        },
        [props],
    );

    return (
        <LoginButton
            code="dynamics"
            getAuthUrl={getAuthUrl}
            paramNames={["code", "state"]}
            requireAllParams
            onSuccess={connect}
            onError={(message) => {
                setIsLoading(false);
                props.setIsButtonLoader?.(false);
                props.onError?.(message);
            }}
            onAuthUrlError={() => {
                setIsLoading(false);
                props.setIsButtonLoader?.(false);
            }}
            logoSrc="/assets/images/typeImages/lead/destinations/microsoft-dynamics-logo.png"
            logoWidth={50}
            buttonText={
                language?.buttons?.connect_with_dynamics ??
                "Connect with Microsoft Dynamics"
            }
            disabled={props?.disabled || isLoading}
            isLoadingExternal={isLoading}
        />
    );
};

export default DynamicsLeadLogin;
